/**
 * CharterLayout — Sidebar secundario del módulo Charter (alquiler de embarcaciones).
 * Acceso restringido a roles: operaciones, admin, superadmin.
 * Sub-secciones: Listado, Calendario, Nuevo.
 * Mismo patrón visual que PasadiasLayout / MuelleLayout.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, List, ListItemButton, ListItemIcon, ListItemText, Chip, Tabs, Tab, Badge } from '@mui/material';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import { getFirebaseAuthHeaders } from '../common/firebaseAuth';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  try { return JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'; }
  catch { return '1'; }
}
function getRole() {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase(); }
  catch { return ''; }
}
async function getAuthHeaders() { return getFirebaseAuthHeaders(); }
const SECTIONS = [
  { path: 'listado',    label: 'Listado',    icon: <EventNoteIcon />,       color: '#7c3aed' },
  { path: 'calendario', label: 'Calendario', icon: <CalendarMonthIcon />,   color: '#8b5cf6' },
  { path: 'nuevo',      label: 'Nuevo',      icon: <AddCircleIcon />,       color: '#a855f7' },
];

const ROLES_PERMITIDOS = ['operaciones', 'admin', 'superadmin'];

export default function CharterLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const empresaId = getEmpresaId();
  const role = getRole();
  const [counts, setCounts] = useState({});

  const cargarCounts = useCallback(async () => {
    try {
      const r = await fetch(
        `${API_BASE}/turismo/charter/reservas?empresa_id=${empresaId}&limit=500`,
        { headers: await getAuthHeaders() }
      );
      if (r.ok) {
        const data = await r.json();
        const items = data.items || [];
        const hoy = new Date().toISOString().slice(0, 10);
        const presupuestos = items.filter(i => i.estado === 'presupuesto').length;
        const proximos = items.filter(i =>
          ['confirmada', 'embarcada'].includes(i.estado) &&
          (i.fecha_alquiler || '') >= hoy
        ).length;
        setCounts({
          listado: { total: items.length },
          calendario: { total: proximos, warn: false },
          nuevo: presupuestos > 0 ? { total: presupuestos, warn: false } : null,
        });
      }
    } catch (e) { console.error('[CharterLayout] counts', e); }
  }, [empresaId]);

  useEffect(() => { cargarCounts(); }, [cargarCounts]);

  // Redirigir a "listado" si entran a /charter sin sub-ruta
  useEffect(() => {
    if (location.pathname === '/charter' || location.pathname === '/charter/') {
      navigate('/charter/listado', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Bloqueo por rol — muestra mensaje amable si no autorizado
  if (!ROLES_PERMITIDOS.includes(role)) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <DirectionsBoatIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
        <Typography sx={{ color: '#1e293b', fontSize: 18, fontWeight: 700, mb: 1 }}>
          Módulo Charter no disponible
        </Typography>
        <Typography sx={{ color: '#64748b', fontSize: 14 }}>
          Este módulo es exclusivo del equipo de operaciones. Contacta a un administrador
          si necesitas acceso.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: '#f8fafc' }}>
      {/* Sidebar secundario */}
      <Box sx={{
        width: 240, flexShrink: 0, bgcolor: '#fff',
        borderRight: '1px solid #e2e8f0',
        display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
      }}>
        <Box sx={{ p: 2.5, pb: 1.5 }}>
          <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
            Charter
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: '#a855f7', fontWeight: 600, mt: 0.5 }}>
            Alquiler de embarcaciones
          </Typography>
        </Box>
        <List sx={{ px: 1, py: 0 }} dense>
          {SECTIONS.map(s => {
            const isActive = location.pathname.includes(`/charter/${s.path}`);
            const c = counts[s.path];
            return (
              <ListItemButton key={s.path} onClick={() => navigate(`/charter/${s.path}`)}
                sx={{
                  borderRadius: 2, mb: 0.5, py: 0.75, pl: 1.5,
                  bgcolor: isActive ? `${s.color}15` : 'transparent',
                  borderLeft: isActive ? `3px solid ${s.color}` : '3px solid transparent',
                  '&:hover': { bgcolor: isActive ? `${s.color}25` : '#f1f5f9' },
                }}>
                <ListItemIcon sx={{ minWidth: 32, color: isActive ? s.color : '#94a3b8' }}>
                  {s.icon}
                </ListItemIcon>
                <ListItemText primary={s.label}
                  primaryTypographyProps={{
                    fontSize: 13, fontWeight: isActive ? 700 : 500,
                    color: isActive ? '#0f172a' : '#475569',
                  }} />
                {c && c.total > 0 && (
                  <Chip size="small" label={c.total}
                    sx={{ height: 18, fontSize: 10, fontWeight: 700,
                          bgcolor: c.warn ? '#fef3c7' : (isActive ? s.color : '#e2e8f0'),
                          color: c.warn ? '#92400e' : (isActive ? '#fff' : '#64748b') }} />
                )}
              </ListItemButton>
            );
          })}
        </List>

        <Box sx={{ flex: 1 }} />
        <Box sx={{ p: 2, borderTop: '1px solid #f1f5f9' }}>
          <Typography sx={{ fontSize: 10.5, color: '#94a3b8', lineHeight: 1.5 }}>
            Los charters bloquean la embarcación completa por un día.
            <strong> Presupuesto</strong> no reserva; <strong>confirmada</strong> sí.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Tabs móviles */}
        <Box sx={{
          display: { xs: 'block', md: 'none' },
          mb: 2, mx: -2, px: 2,
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fff',
          position: 'sticky', top: -16, zIndex: 5,
        }}>
          <Tabs
            value={Math.max(0, SECTIONS.findIndex(s => location.pathname.includes(`/charter/${s.path}`)))}
            onChange={(_, idx) => navigate(`/charter/${SECTIONS[idx].path}`)}
            variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            sx={{
              minHeight: 44,
              '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 700, fontSize: 12, px: 1.5 },
              '& .MuiTabs-indicator': { backgroundColor: '#7c3aed', height: 3, borderRadius: 2 },
            }}
          >
            {SECTIONS.map(s => {
              const c = counts[s.path];
              const n = c?.total || 0;
              return (
                <Tab key={s.path}
                  icon={s.icon} iconPosition="start"
                  label={
                    <Badge badgeContent={n} max={99}
                      sx={{
                        '& .MuiBadge-badge': {
                          bgcolor: c?.warn ? '#f59e0b' : s.color, color: '#fff',
                          fontSize: 9, height: 16, minWidth: 16, right: -6, top: 2,
                        },
                      }}>
                      <span style={{ paddingRight: n > 0 ? 10 : 0 }}>{s.label}</span>
                    </Badge>
                  }
                />
              );
            })}
          </Tabs>
        </Box>

        <Outlet context={{ refreshCounts: cargarCounts, empresaId }} />
      </Box>
    </Box>
  );
}
